import Button from '@atlaskit/button';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import { ButtonItem, MenuGroup, Section } from '@atlaskit/menu';
import { type TriggerProps } from '@atlaskit/popup';
import { Box, Text, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import Popup, { type PopupProps } from '@databases/atlaskit/Popup';
import { getFieldType, getFieldTypes, FieldTypeCategory } from '@databases/fields';
import { FieldTypeIcon } from '@databases/icons/FieldTypeIcon';
import { messages } from '@databases/intl/messages';
import { useComponent, useResizeObserver } from '@databases/react';
import { DatabaseFieldType } from '@databases/types/database';
import useHandleOutsideClick from '@databases/utils/hooks/useHandleOutsideClick';
import { legacyColors } from '@databases/utils/legacy-colors';
import composeRefs from '@seznam/compose-react-refs';
import React, {
  type FunctionComponent,
  type ReactElement,
  useCallback,
  useMemo,
  useRef,
  useState
} from 'react';
import { useIntl } from 'react-intl-next';
import styled from 'styled-components';

import { getExperimentValue } from '@canvas/client-feature-flags';

import FieldTypeSearch from './FieldTypeSearch.tsx';

const StyledButton = styled(Button).withConfig({
  shouldForwardProp: (prop) => prop !== 'isInvalid'
})`
  && {
    text-align: left;
    align-items: center;
    padding: ${token('space.050', '4px')} ${token('space.100', '8px')};
    border-radius: ${token('radius.small', '3px')};
    ${({ shouldFitContainer }) => shouldFitContainer && `height: 40px`};
    ${({ isSelected }) =>
      isSelected
        ? 'border: none'
        : `border: ${token('border.width.selected', '2px')} solid ${token(
            'color.border.input',
            legacyColors.N40
          )}`};
    ${({ isInvalid }) =>
      isInvalid && `border-color: ${token('color.border.danger', '#E2483D')}`};
  }
`;

const PopupContentContainer = styled.div``;

const contentStyles = xcss({
  overflow: 'auto'
});

const unreachable = (value: never) => {
  throw new Error(`Not implemented: ${value}`);
};

export interface FieldTypeTriggerProps extends TriggerProps {
  onFocus?: (e?: React.FocusEvent<HTMLElement>) => void;
  onBlur?: (e?: React.FocusEvent<HTMLElement>) => void;
  onClick?: (e?: React.MouseEvent<HTMLElement>) => void;
  isSelected?: boolean;
}

export type FieldTypeDropdownProps = Omit<
  PopupProps,
  'trigger' | 'isOpen' | 'content'
> & {
  value?: DatabaseFieldType;
  onChange: (value: DatabaseFieldType) => void;
  onFocus?: (e?: React.FocusEvent<HTMLElement>) => void;
  onBlur?: (e?: React.FocusEvent<HTMLElement>) => void;
  trigger?: string | ((triggerButtonProps: FieldTypeTriggerProps) => ReactElement);
  shouldFitContainer?: boolean;
  isDisabled?: boolean;
  hasFieldTypeError?: boolean;
  ariaDescribedBy?: string;
  ariaLabelledBy?: string;

  /** If specified, only these types are selectable. Otherwise all existing types are selectable. */
  showTypes?: DatabaseFieldType[];
  maxHeight?: string;
  label?: string;
};

/**
 * Renders a dropdown menu that allows selecting a database field type.
 */
const FieldTypeDropdown: FunctionComponent<FieldTypeDropdownProps> = ({
  maxHeight = '435px',
  value,
  onChange,
  trigger,
  onFocus,
  onBlur,
  shouldFitContainer,
  showTypes,
  isDisabled,
  hasFieldTypeError,
  ariaDescribedBy,
  ariaLabelledBy,
  label,
  ...props
}) => {
  const [searchValue, setSearchValue] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  const { formatMessage } = useIntl();

  const [isOpen, setIsOpen] = useState(false);
  const handleOpenChange = useCallback(() => {
    setIsOpen((isOpen) => {
      if (isOpen) {
        setSearchValue('');
      }
      return !isOpen;
    });
  }, []);
  const [outerTrigger, setOuterTrigger] = useState<HTMLElement | null>(null);
  const menuWidth = useResizeObserver(
    shouldFitContainer ? outerTrigger : undefined
  )?.width;
  const isCheckboxEnabled = getExperimentValue('checkboxFieldType', 'enabled', {
    fireExperimentExposure: true
  });
  const handleClose = useCallback(() => {
    setIsOpen(false);
    setSearchValue('');
  }, [setIsOpen, setSearchValue]);

  useHandleOutsideClick(
    handleClose,
    dropdownRef,
    ['[id="field-type-dropdown-trigger"]'],
    isOpen
  );

  const triggerComp = useComponent(
    ({ ref, ...props }: TriggerProps) => {
      if (trigger && typeof trigger === 'function') {
        return trigger({
          ref: composeRefs((r) => setOuterTrigger(r), ref),
          onFocus,
          onBlur,
          onClick: handleOpenChange,
          isSelected: props['aria-expanded'],
          ...props
        });
      }

      if (typeof trigger === 'string') {
        return (
          // FIXME: Accessibility issue - jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
          // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
          <span ref={ref} onClick={handleOpenChange} {...props}>
            {trigger}
          </span>
        );
      }

      const valueLabel = value ? getFieldType(value).label : null;

      return (
        <StyledButton
          id="field-type-dropdown-trigger"
          testId="field-type-dropdown-trigger"
          iconBefore={
            valueLabel && value ? (
              <FieldTypeIcon type={value} label={valueLabel} noTooltip />
            ) : undefined
          }
          iconAfter={<ChevronDownIcon spacing="spacious" label="" />}
          ref={composeRefs((r) => setOuterTrigger(r), ref)}
          onFocus={onFocus}
          onBlur={onBlur}
          shouldFitContainer={shouldFitContainer}
          onClick={handleOpenChange}
          isSelected={props['aria-expanded']}
          isDisabled={isDisabled}
          appearance="subtle"
          isInvalid={hasFieldTypeError}
          aria-label={label}
          aria-describedby={ariaDescribedBy}
          aria-labelledby={ariaLabelledBy}
          {...props}
        >
          {trigger ?? (valueLabel && formatMessage(valueLabel))}
        </StyledButton>
      );
    },
    [
      formatMessage,
      handleOpenChange,
      hasFieldTypeError,
      isDisabled,
      onBlur,
      onFocus,
      shouldFitContainer,
      trigger,
      value,
      label,
      ariaDescribedBy,
      ariaLabelledBy
    ]
  );

  const getCategoryName = useMemo(
    () => (cat: FieldTypeCategory) => {
      switch (cat) {
        case FieldTypeCategory.BASIC:
          return messages['field-type.category-basic'];
        case FieldTypeCategory.PAGE:
          return messages['field-type.category-page-and-live-doc'];
        case FieldTypeCategory.JIRA:
          return messages['field-type.category-jira'];
        case FieldTypeCategory.OTHER_DATABASES:
          return messages['field-type.category-other-databases'];
        default:
          return unreachable(cat);
      }
    },
    []
  );

  const items = useMemo(
    () =>
      Object.values(FieldTypeCategory)
        .map(
          (cat) =>
            [
              formatMessage(getCategoryName(cat)),
              Object.entries(getFieldTypes()).filter(([type, data]) => {
                if (type === DatabaseFieldType.AI_SUMMARY) {
                  return false;
                }
                if (!isCheckboxEnabled && type === 'checkbox') {
                  return false;
                }
                return (
                  data.category === cat &&
                  (!showTypes || showTypes.includes(type as DatabaseFieldType)) &&
                  formatMessage(data.label)
                    .toLowerCase()
                    .includes(searchValue.toLowerCase())
                );
              })
            ] as const
        )
        .filter(([_, types]) => types.length > 0),
    [formatMessage, getCategoryName, isCheckboxEnabled, searchValue, showTypes]
  );

  return (
    <Popup
      isOpen={isOpen}
      onClose={handleOpenChange}
      contentContainer={PopupContentContainer}
      trigger={triggerComp}
      content={({ setInitialFocusRef }) => (
        <Box ref={dropdownRef}>
          <MenuGroup
            testId="field-type-dropdown"
            minWidth={menuWidth ? menuWidth : undefined}
            maxHeight={maxHeight}
            onClick={(e) => e.stopPropagation()}
          >
            <FieldTypeSearch
              onChange={setSearchValue}
              value={searchValue}
              ref={setInitialFocusRef}
            />
            {/* eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage */}
            <Box xcss={contentStyles} style={{ marginTop: '37px' }}>
              {items.map(([cat, types]) => (
                <Section
                  key={cat}
                  title={items.length > 1 ? cat : undefined}
                  hasSeparator
                >
                  {types.map(([type, { label, shortDescription }]) => (
                    <Tooltip
                      key={type}
                      content={shortDescription && formatMessage(shortDescription)}
                      hideTooltipOnMouseDown
                      position="left"
                    >
                      <ButtonItem
                        data-testid={`field-type-dropdown-${type}`}
                        iconBefore={
                          <FieldTypeIcon
                            type={type as DatabaseFieldType}
                            label={label}
                            noTooltip
                          />
                        }
                        onClick={() => {
                          onChange(type as DatabaseFieldType);
                          handleOpenChange();
                        }}
                        isSelected={type === value}
                      >
                        {formatMessage(label)}
                      </ButtonItem>
                    </Tooltip>
                  ))}
                </Section>
              ))}
            </Box>
            {!items.length && (
              <Box paddingBlock="space.200" paddingInline="space.200">
                <Text color="color.text.subtlest">
                  {formatMessage(
                    messages['field-settings.field-type-dropdown-no-matches-text']
                  )}
                </Text>
              </Box>
            )}
          </MenuGroup>
        </Box>
      )}
      {...props}
    />
  );
};

export default FieldTypeDropdown;
